import { SelectOption } from '@roc/ui';
import { borrowerEntityTypeOptions, statesAbbreviation, statesNames } from '@roc/feature-utils';
import {
  ARTICLES_OF_INCORPORATION,
  BORROWER_LLC_CERTIFICATE_OF_GOOD_STANDING,
  BORROWING_CORP_STOCK_CERTIFICATE,
  BYLAWS,
  CERTIFICATE_OF_EXISTENCE,
  CERTIFICATE_OF_FORMATION,
  CERTIFICATE_OF_GOOD_STANDING,
  CERTIFICATE_OF_GOOD_STANDING_BASE,
  EIN_LETTER,
  FOREIGN_ENTITY_REGISTRATION,
  OPERATING_AGREEMENT,
  CERT_OF_FORMATION_OR_INC,
  CERT_OF_GOOD_STANDING_OR_EXISTENCE,
  OPERATING_AGREEMENT_BYLAWS_STOCK,
} from '@roc/feature-documents';

export type EntityFieldType =
  | 'text'
  | 'entityType'
  | 'state'
  | 'date'
  | 'yesNo'
  | 'states'
  | 'address'
  | 'borrowers'
  | 'ein';

export const ENTITY_FIELD_TYPES: Record<string, EntityFieldType> = {
  borrowerEntityName: 'text',
  entityType: 'entityType',
  jurisdictionState: 'state',
  dateOfIncorporation: 'date',
  borrowerEntityAddress: 'address',
  borrowers: 'borrowers',
  cogsDate: 'date',
  operatingAgreement: 'yesNo',
  einNumber: 'ein',
  isAuthentic: 'yesNo',
  registrationState: 'states',
};

export interface EntityFieldSchema {
  itemName: string;
  itemDisplayName: string;
  required?: boolean;
  format?: { pattern: RegExp; message: string };
  blankWhenNotExtracted?: boolean;
}

export const EIN_FORMAT = {
  pattern: /^\d{2}-\d{7}$/,
  message: 'The EIN must have the format xx-xxxxxxx',
};

const BORROWERS_FIELD: EntityFieldSchema = {
  itemName: 'borrowers',
  itemDisplayName: 'Borrowers',
};

const FORMATION_FIELDS: EntityFieldSchema[] = [
  {
    itemName: 'borrowerEntityAddress',
    itemDisplayName: 'Borrower Entity Address',
    blankWhenNotExtracted: true,
  },
  {
    itemName: 'dateOfIncorporation',
    itemDisplayName: 'Date of Incorporation',
    blankWhenNotExtracted: true,
  },
  {
    itemName: 'jurisdictionState',
    itemDisplayName: 'Jurisdiction State',
    blankWhenNotExtracted: true,
  },
  {
    itemName: 'entityType',
    itemDisplayName: 'Entity Type',
    blankWhenNotExtracted: true,
  },
];

const GOOD_STANDING_FIELDS: EntityFieldSchema[] = [
  {
    itemName: 'cogsDate',
    itemDisplayName: 'COGS Date',
    required: true,
    blankWhenNotExtracted: true,
  },
];

const OWNERSHIP_FIELDS: EntityFieldSchema[] = [BORROWERS_FIELD];

const EIN_FIELDS: EntityFieldSchema[] = [
  {
    itemName: 'einNumber',
    itemDisplayName: 'EIN Number',
    required: true,
    format: EIN_FORMAT,
  },
];

const FOREIGN_ENTITY_FIELDS: EntityFieldSchema[] = [
  {
    itemName: 'registrationState',
    itemDisplayName: 'Registration State',
    required: true,
    blankWhenNotExtracted: true,
  },
];

export const fieldsForDocument = (documentName: string): EntityFieldSchema[] =>
({
  [EIN_LETTER]: EIN_FIELDS,
  [FOREIGN_ENTITY_REGISTRATION]: FOREIGN_ENTITY_FIELDS,
  [CERTIFICATE_OF_FORMATION]: FORMATION_FIELDS,
  [ARTICLES_OF_INCORPORATION]: FORMATION_FIELDS,
  [CERTIFICATE_OF_GOOD_STANDING]: GOOD_STANDING_FIELDS,
  [CERTIFICATE_OF_GOOD_STANDING_BASE]: GOOD_STANDING_FIELDS,
  [CERTIFICATE_OF_EXISTENCE]: GOOD_STANDING_FIELDS,
  [BORROWER_LLC_CERTIFICATE_OF_GOOD_STANDING]: GOOD_STANDING_FIELDS,
  [OPERATING_AGREEMENT]: OWNERSHIP_FIELDS,
  [BYLAWS]: OWNERSHIP_FIELDS,
  [BORROWING_CORP_STOCK_CERTIFICATE]: OWNERSHIP_FIELDS,
  [CERT_OF_FORMATION_OR_INC]: FORMATION_FIELDS,
  [CERT_OF_GOOD_STANDING_OR_EXISTENCE]: GOOD_STANDING_FIELDS,
  [OPERATING_AGREEMENT_BYLAWS_STOCK]: OWNERSHIP_FIELDS,
}[documentName] ?? []);

export const ADDRESS_KEYS = [
  'streetNumber',
  'streetName',
  'aptNumber',
  'city',
  'state',
  'zipCode',
];

export const ADDRESS_LABELS: Record<string, string> = {
  streetNumber: 'Street Number',
  streetName: 'Street Name',
  aptNumber: 'Apt / Unit',
  city: 'City',
  state: 'State',
  zipCode: 'Zip Code',
};

export const stateSelectOptions: SelectOption[] = statesAbbreviation.map(
  (abbreviation, i) => ({
    value: abbreviation,
    label: statesNames[i],
  })
);

export const entityTypeSelectOptions: SelectOption[] = borrowerEntityTypeOptions;

export const yesNoSelectOptions: SelectOption[] = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

export const statesToList = (value: any): string[] => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (!value) return [];
  return String(value)
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
};

export const statesToCsv = (value: any): string => statesToList(value).join(', ');

export const borrowerRows = (value: any): any[] => (Array.isArray(value) ? value : []);

export const fieldTypeOf = (itemName: string): EntityFieldType =>
  ENTITY_FIELD_TYPES[itemName] ?? 'text';

export const addressLine = (value: any): string => {
  const street = [value?.streetNumber, value?.streetName, value?.aptNumber]
    .filter(Boolean)
    .join(' ');
  return [street, value?.city, value?.state, value?.zipCode]
    .filter(part => part && String(part).trim())
    .join(', ');
};

export const formatAddress = (value: any): string => {
  if (!value) return '';
  const street = [value.streetNumber, value.streetName].filter(Boolean).join(' ');
  return [street, value.aptNumber, value.city, value.state, value.zipCode]
    .filter(part => part && String(part).trim())
    .join(', ');
};

export const formatBorrowers = (value: any): string => {
  if (!Array.isArray(value) || !value.length) return '';
  return value
    .map(row => {
      const pct = row?.ownershipPercentage;
      return pct ? `${row?.borrowerName ?? ''} (${pct}%)` : row?.borrowerName ?? '';
    })
    .join(', ');
};

export const formatEntityValue = (itemName: string, value: any): any => {
  switch (fieldTypeOf(itemName)) {
    case 'states':
      return statesToCsv(value);
    case 'address':
      return formatAddress(value);
    case 'borrowers':
      return formatBorrowers(value);
    case 'yesNo':
      if (value === null || value === undefined || value === '') return value;
      return value === true || value === 'true' ? 'Yes' : 'No';
    default:
      return value;
  }
};
